/**
 * ─────────────────────────────────────────────────────────────────────────
 *  SVARA CHAKRA — SERVICE CATALOG & PAYMENT CONFIGURATION  (edit this file only)
 * ─────────────────────────────────────────────────────────────────────────
 *
 *  Fully STATIC (no backend) & Hostinger-compatible. Single source of truth for
 *  CATEGORIES, individual SERVICES, "Energy Exchange" values, "Session
 *  Reservation" amounts, and Razorpay no-backend payment identifiers.
 *  The UI reads everything from here — NEVER hardcode a value in a component.
 *
 *  Only PUBLIC Razorpay identifiers belong here. NEVER put the Key SECRET on
 *  the frontend.
 *
 *  ── LANGUAGE ────────────────────────────────────────────────────────────
 *    Use only: "Energy Exchange", "Session Reservation", "Reserve Your Session".
 *    Never: Fee / Charges / Price / Cost / Booking Amount / Confirmation Payment.
 *
 *  ── CATEGORY TYPES ──────────────────────────────────────────────────────
 *    "booking"  → visitors reserve a session with a Session Reservation amount.
 *    "enquiry"  → no payment; visitors submit interest and we contact them.
 *
 *  ── SERVICE FIELDS ──────────────────────────────────────────────────────
 *    id, category, name
 *    duration            e.g. "60 minutes"  (placeholder — edit freely)
 *    mode                "Online" | "In-Person" | "Both"
 *    energyExchange      Total Energy Exchange, whole rupees (null for enquiry)
 *    sessionReservation  Amount to reserve the appointment, whole rupees
 *    unit                optional suffix, e.g. "Per Session"
 *    paymentType         "reservation" | "full" | "enquiry"
 *    razorpay            { paymentLinkUrl, paymentButtonId }  (public only)
 *    mode_override       (optional) per-service payment mode override
 *
 *  Remaining Energy Exchange = energyExchange − sessionReservation (derived).
 *
 *  ── FUTURE FastAPI BACKEND ──────────────────────────────────────────────
 *    Set PAYMENT_DEFAULTS.mode = "backend_checkout" and keyId. paymentService.js
 *    already has the forward-compatible branch — the UI does NOT change.
 * ─────────────────────────────────────────────────────────────────────────
 */

export const CURRENCY = "INR";
export const LOCALE = "en-IN";

// Referenced by the numerology → contact prefill so a future rename of these
// ids stays in one place.
export const NUMEROLOGY_SERVICE_ID = "numerology-consultation";
export const PERSONAL_CATEGORY_ID = "personal";

export const PAYMENT_DEFAULTS = {
  provider: "razorpay",
  mode: "payment_link", // "payment_link" | "payment_button" | "backend_checkout"
  keyId: "", // future backend checkout — PUBLIC Key ID only

  reserveHeading: "Reserve Your Session",
  // {reservation} is replaced with the selected service's Session Reservation.
  reserveSupportingText:
    "To reserve your preferred appointment, a Session Reservation of {reservation} is required. The remaining Energy Exchange may be completed before or during your session.",
  appointmentNote:
    "Your appointment is confirmed immediately after your Session Reservation is successfully received.",
  securityNote: "Payments are processed securely through Razorpay.",
  enquiryMessage:
    "Thank you for sharing your interest with Svara Chakra. We truly appreciate you reaching out to us. Our team will carefully review your requirements and contact you shortly to discuss your goals and create an experience thoughtfully tailored to your needs.",
};

// Categories are for browsing only — never used directly for payment.
export const CATEGORIES = [
  { id: "personal", name: "Personal Wellness", type: "booking" },
  { id: "corporate", name: "Corporate Wellness", type: "enquiry" },
  { id: "retreats", name: "Retreats", type: "enquiry" },
  { id: "learning", name: "Learning", type: "enquiry" },
];

// Individual services (the actual bookable / enquirable offerings).
// NOTE: `duration` and `mode` values are sensible PLACEHOLDERS — edit freely.
export const SERVICES = [
  // ── Personal Wellness (booking → Session Reservation) ────────────────────
  {
    id: "initial-consultation-remedies",
    category: "personal",
    name: "Initial Consultation with Remedies",
    duration: "60 Minutes",
    mode: "Both",
    energyExchange: 1299,
    sessionReservation: 299,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "numerology-consultation",
    category: "personal",
    name: "Numerology Consultation",
    duration: "75 Minutes",
    mode: "Both",
    energyExchange: 2699,
    sessionReservation: 499,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "individual-music-therapy",
    category: "personal",
    name: "Individual Music Therapy",
    duration: "60 Minutes",
    mode: "In-Person",
    energyExchange: 999,
    sessionReservation: 199,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "online-music-therapy",
    category: "personal",
    name: "Online Music Therapy",
    duration: "60 Minutes",
    mode: "Online",
    energyExchange: 799,
    sessionReservation: 199,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "reiki-healing",
    category: "personal",
    name: "Reiki Healing",
    duration: "45 Minutes",
    mode: "Both",
    energyExchange: 555,
    sessionReservation: 199,
    unit: "Per Session",
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "chakra-aura-cleansing",
    category: "personal",
    name: "Chakra & Aura Cleansing",
    duration: "60 Minutes",
    mode: "Both",
    energyExchange: 1111,
    sessionReservation: 299,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "wish-fulfilment-reiki",
    category: "personal",
    name: "Wish Fulfilment Reiki",
    duration: "45 Minutes",
    mode: "Online",
    energyExchange: 1111,
    sessionReservation: 299,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "tarot-reading-3",
    category: "personal",
    name: "Tarot Reading (3 Questions)",
    duration: "30 Minutes",
    mode: "Online",
    energyExchange: 999,
    sessionReservation: 199,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },
  {
    id: "tarot-reading-5",
    category: "personal",
    name: "Tarot Reading (5 Questions)",
    duration: "45 Minutes",
    mode: "Online",
    energyExchange: 1299,
    sessionReservation: 299,
    paymentType: "reservation",
    razorpay: { paymentLinkUrl: "", paymentButtonId: "" },
  },

  // ── Corporate Wellness (enquiry) ─────────────────────────────────────────
  { id: "corporate-wellness-workshop", category: "corporate", name: "Corporate Wellness Workshop", paymentType: "enquiry" },
  { id: "employee-wellness-session", category: "corporate", name: "Employee Wellness Session", paymentType: "enquiry" },
  { id: "leadership-through-sound", category: "corporate", name: "Leadership Through Sound", paymentType: "enquiry" },

  // ── Retreats (enquiry) ───────────────────────────────────────────────────
  { id: "one-day-retreat", category: "retreats", name: "One-Day Retreat", paymentType: "enquiry" },
  { id: "weekend-retreat", category: "retreats", name: "Weekend Retreat", paymentType: "enquiry" },
  { id: "residential-retreat", category: "retreats", name: "Residential Retreat", paymentType: "enquiry" },

  // ── Learning (enquiry) ───────────────────────────────────────────────────
  { id: "reiki-level-1", category: "learning", name: "Reiki Level 1", paymentType: "enquiry" },
  { id: "reiki-level-2", category: "learning", name: "Reiki Level 2", paymentType: "enquiry" },
  { id: "numerology-foundation", category: "learning", name: "Numerology Foundation", paymentType: "enquiry" },
  { id: "music-therapy-foundation", category: "learning", name: "Music Therapy Foundation", paymentType: "enquiry" },
];

/* ── Helpers (keep pricing logic out of components) ── */

export const getCategory = (categoryId) =>
  CATEGORIES.find((c) => c.id === categoryId) || null;

export const getServicesByCategory = (categoryId) =>
  SERVICES.filter((s) => s.category === categoryId);

export const getService = (serviceId) =>
  SERVICES.find((s) => s.id === serviceId) || null;

export const isEnquiryCategory = (categoryId) =>
  getCategory(categoryId)?.type === "enquiry";

export const isEnquiryService = (service) =>
  !service || service.paymentType === "enquiry";

/** The Session Reservation amount for a service. */
export const getSessionReservation = (service) =>
  service?.sessionReservation || 0;

/** The amount charged now: reservation → sessionReservation; full → energyExchange. */
export const getChargeAmount = (service) => {
  if (!service) return 0;
  if (service.paymentType === "full") return service.energyExchange || 0;
  return getSessionReservation(service);
};

/** Remaining Energy Exchange after the Session Reservation. */
export const getRemainingAmount = (service) => {
  if (!service || service.paymentType !== "reservation") return 0;
  return Math.max(0, (service.energyExchange || 0) - getSessionReservation(service));
};

/** Active payment mode for a service (per-service override or global). */
export const getMode = (service) =>
  (service && service.mode_override) || PAYMENT_DEFAULTS.mode;

/** Format a rupee value using the configured locale/currency. */
export const formatMoney = (value) =>
  new Intl.NumberFormat(LOCALE, {
    style: "currency",
    currency: CURRENCY,
    maximumFractionDigits: 0,
  }).format(value || 0);
